import React, { useEffect, useState, useCallback, useRef } from 'react';
import './ReportGen.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFlask, faDna, faBaby, faTooth, faDroplet, faEllipsisV, faLayerGroup,
  faChevronCircleRight
} from '@fortawesome/free-solid-svg-icons';
import TempInputRow from './TempInputRow';
import RecentReports from './RecentReports';
import StorageInfo from './StorageInfo/StorageInfo';

const INITIAL_LOAD_LIMIT = 50; // Initial items
const PAGINATION_LIMIT = 25;    // Items per scroll-triggered load
const RECENT_REFRESH_LIMIT = 50;// Top items to refresh
const REFRESH_INTERVAL = 3000;  // 3 seconds

function ReportGen({ user }) {
  const [items, setItems] = useState([]);
  const [inputRows, setInputRows] = useState([]);
  const [invalidRows, setInvalidRows] = useState(new Set());
  const [flashInvalidRows, setFlashInvalidRows] = useState(false);
  
  // This state controls the filtering. Set to true for beta reports (ClinicianID = 0).
  const [fetchBetaOnly, setFetchBetaOnly] = useState(false); //HEEEEEEEEEEEEEEERE 

  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  const observerTarget = useRef(null); // For IntersectionObserver

  // --- DATA FETCHING LOGIC ---

  const fetchData = useCallback(async (page, limit, isInitialLoad = false) => {
    if (isLoadingMore && !isInitialLoad) return;
    
    if (isInitialLoad) setIsLoadingInitial(true);
    else setIsLoadingMore(true);

    try {
      // Build the URL based on the fetchBetaOnly state
      let apiUrl = `https://service3.szapfs.org/api/report_requests?page=${page}&limit=${limit}`;
      if (fetchBetaOnly) {
        apiUrl += '&betaOnly=true';
      }

      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      
      setItems(prevItems => (page === 1 ? result.data : [...prevItems, ...result.data]));
      setCurrentPage(result.pagination.currentPage);
      setHasNextPage(result.pagination.hasNextPage);
      if (isInitialLoad) setInitialDataLoaded(true);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      if (isInitialLoad) setIsLoadingInitial(false);
      else setIsLoadingMore(false);
    }
  }, [isLoadingMore, fetchBetaOnly]); // Depends on fetchBetaOnly to rebuild the function with the correct URL logic

  // EFFECT 1: Initial data load. Runs once on mount and re-runs if fetchBetaOnly changes.
  useEffect(() => {
    fetchData(1, INITIAL_LOAD_LIMIT, true);
  }, [fetchData]); 

  // EFFECT 2: Periodic refresh of the top N items.
  useEffect(() => {
    if (!initialDataLoaded) return;

    const refreshRecentItems = async () => {
      try {
        // Build the URL based on the fetchBetaOnly state for the refresh endpoint
        let apiUrl = `https://service3.szapfs.org/api/report_requests/recent?limit=${RECENT_REFRESH_LIMIT}`;
        if (fetchBetaOnly) {
          apiUrl += '&betaOnly=true';
        }

        const response = await fetch(apiUrl);
        if (!response.ok) {
          console.error(`Error fetching recent items: ${response.status}`);
          return;
        }
        const recentData = await response.json();

        setItems(prevItems => {
          const recentItemIDs = new Set(recentData.map(item => item.ReportID));
          const olderItemsToKeep = prevItems.filter(item => !recentItemIDs.has(item.ReportID));
          const combinedItems = [...recentData, ...olderItemsToKeep];
          return combinedItems.sort((a, b) => Number(b.ReportID) - Number(a.ReportID));
        });
      } catch (error) {
        console.error('Error in refreshRecentItems interval:', error);
      }
    };

    const intervalId = setInterval(refreshRecentItems, REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [initialDataLoaded, fetchBetaOnly]); // Re-creates interval if beta mode changes

  // EFFECT 3: Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasNextPage && !isLoadingMore && !isLoadingInitial) {
          fetchData(currentPage + 1, PAGINATION_LIMIT);
        }
      },
      { threshold: 1.0 } // Trigger when 100% of the target is visible
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasNextPage, isLoadingMore, isLoadingInitial, currentPage, fetchData]);

  // --- QUEUE MANAGEMENT LOGIC ---

  const addNewInputRow = () => {
    const newRow = {
      id: Date.now(), PatientFirstName: '', PatientLastName: '', ReportType: '',
      PatientDateOfBirth: '', PatientGender: '', Preset: '', Details: '',
      Company: '', Language: '', Newborn: {}, PharmJson: {},
    };
    setInputRows([...inputRows, newRow]);
  };

  const setNewReport = (id, updatedReport) => {
    setInputRows(prevInputRows =>
      prevInputRows.map(row => (row.id === id ? { ...row, ...updatedReport } : row))
    );
  };

  const validateReport = (report) => {
    const baseValid = (
      report.Preset && report.isValidId && report.ReportType &&
      report.Company && report.Language
    );
    if (!baseValid) return false;
    if (report.ReportType === "Acıbadem Pharmacogenomics") {
      return report.PharmJson && typeof report.PharmJson === "object" && Object.keys(report.PharmJson).length > 0;
    }
    return true;
  };

  const handleRunQueue = () => {
    const newInvalidRows = new Set();
    const validReports = [];
    inputRows.forEach((report) => {
      if (!validateReport(report)) newInvalidRows.add(report.id);
      else validReports.push(report);
    });
    setInvalidRows(newInvalidRows);
    if (newInvalidRows.size > 0) {
      setFlashInvalidRows(true);
      setTimeout(() => setFlashInvalidRows(false), 600);
    }

    const languageMap = { Russian: 'RUS', Japanese: 'JPN', Korean: 'KOR', English: 'ENG', Turkish: 'TUR', Arabic: 'ARB', Spanish: 'SPA', Chinese: 'CHN' };
    const postReports = validReports.map(report => {
      const truncatedLanguage = languageMap[report.Language] || report.Language;
      const hasPharm = report.PharmJson && typeof report.PharmJson === "object" && Object.keys(report.PharmJson).length > 0;
      const processedReport = {
        ...report,
        ReportType: `${report.ReportType} (${truncatedLanguage})`,
        Details: hasPharm ? report.PharmJson : (report.Newborn || {}),
        Company: report.Company, Language: report.Language,
      };
      delete processedReport.isValidId;
      delete processedReport.Newborn;
      delete processedReport.PharmJson;
      return fetch('https://service3.szapfs.org/api/reportinputreq', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(processedReport),
      });
    });
  
    Promise.all(postReports)
      .then(responses => Promise.all(responses.map(res => res.json())))
      .then(results => {
        console.log("Queue responses received:", results);
        setInputRows(inputRows.filter(row => !validateReport(row)));
      })
      .catch(error => console.error('Error running queue:', error));
  };

  const handleDeleteReport = (id) => {
    setInputRows(inputRows.filter(row => row.id !== id));
    const newInvalidRows = new Set(invalidRows);
    newInvalidRows.delete(id);
    setInvalidRows(newInvalidRows);
  };

  const isRunButtonEnabled = inputRows.length > 0;

  // --- RENDER ---

  return (
    <div className="DRH">
      <div className="DRHL">
        <div className="queue-section-title">
          <div>
            <div className="queue1">
              Queue
              <div
                className={`run-btn ${!isRunButtonEnabled ? 'disabled' : ''}`}
                onClick={isRunButtonEnabled ? handleRunQueue : undefined}
              >
                Run <FontAwesomeIcon icon={faChevronCircleRight} className="iconrun" />
              </div>
            </div>
          </div>
          <div className="queue-actions">
            <div className="addjr" onClick={addNewInputRow}>
              <div className="addjr1"><FontAwesomeIcon icon={faLayerGroup} /></div>
              <button className="add-job-btn">Add Report</button>
            </div>
          </div>
        </div>
        <div className="queue-section">
          {inputRows.length > 0 ? (
            inputRows.map(report => (
              <TempInputRow
                key={report.id} report={report} setNewReport={setNewReport}
                handleDeleteReport={handleDeleteReport} isInvalid={invalidRows.has(report.id)}
                flash={flashInvalidRows && invalidRows.has(report.id)}
              />
            ))
          ) : (
            <div className="no-reports-placeholder">No pending reports</div>
          )}
        </div>
  
        <div className="recents-section-title">Files</div>
        <div className="DRHLTOP">
          <div className="DRHLTOPBOX">
            <div className="DRHLTOPBOXL"><input type="text" placeholder="Search" /></div>
            <div className="DRHLTOPBOXR">▼ Filter</div>
          </div>
        </div>
        <div className="recents-section">
          {isLoadingInitial && <p className="loading-indicator">Loading reports...</p>}
          {!isLoadingInitial && items.length === 0 && (
            <p className="no-reports-indicator">No reports found.</p>
          )}
          
          {!isLoadingInitial && items.length > 0 && (
            <RecentReports items={items} />
          )}
          
          {isLoadingMore && <p className="loading-indicator">Loading more reports...</p>}
          
          {hasNextPage && !isLoadingInitial && (
             <div ref={observerTarget} style={{ height: '1px', margin: '1px' }} />
          )}
        </div>
      </div>
      <div className="DRHR">
       {/* <StorageInfo /> */}
      </div>
    </div>
  );
}

export default ReportGen;